import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";

import useClips from "../../../hooks/useClips";
import useLanguage from "../../../hooks/useLanguage";
import useToaster from "../../../hooks/useToaster";

import ConfirmGenerateDialog from "./ConfirmGenerateDialog";
import TrimTimeline from "./TrimTimeline";
import {
  LTR,
  MAX_CLIP_SEC,
  MIN_CLIP_SEC,
  clipBounds,
  fmtClock,
  fmtWall,
  parseClock,
  showWindow,
  withEnd,
  withStart,
} from "../utils";

const DEFAULT_CLIP_SEC = 5 * 60;

// Authoritative only while focused — otherwise the slider and the I/O keys own
// the value and the field just mirrors them.
function TimeField({ label, value, maxSec, onCommit, text }) {
  const [draft, setDraft] = useState(null);
  const [invalid, setInvalid] = useState(false);

  // `keep` is for Enter: hold the bad text so it can be corrected in place. On
  // blur we revert instead — a rejected draft left in the box would keep
  // displaying a time the clip no longer has as the slider moves underneath it.
  const commit = (keep) => {
    if (draft === null) return;
    const parsed = parseClock(draft, maxSec);
    if (parsed === null) {
      if (keep) {
        setInvalid(true);
      } else {
        setDraft(null);
        setInvalid(false);
      }
      return;
    }
    setInvalid(false);
    setDraft(null);
    onCommit(parsed);
  };

  return (
    <TextField
      label={label}
      value={draft ?? fmtClock(value)}
      size="small"
      error={invalid}
      helperText={invalid ? text.invalidTime : " "}
      onChange={(e) => {
        setDraft(e.target.value);
        setInvalid(false);
      }}
      onBlur={() => commit(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit(true);
        if (e.key === "Escape") {
          setDraft(null);
          setInvalid(false);
        }
        e.stopPropagation(); // typing "i"/"o" must not fire the mark shortcuts
      }}
      slotProps={{
        htmlInput: { style: { ...LTR, fontVariantNumeric: "tabular-nums" } },
      }}
      sx={{ width: 116 }}
    />
  );
}

export default function ClipEditor({ show, text, onBack, onGenerated }) {
  const [{ direction }] = useLanguage();
  const { generateFromClip } = useClips();
  const { addToaster } = useToaster();
  const videoRef = useRef(null);
  const previewingRef = useRef(false);
  const playheadRef = useRef(null); // last position we asked for, to skip redundant seeks

  const [duration, setDuration] = useState(show.recordingDurationSec);
  const bounds = useMemo(() => clipBounds(show, duration), [show, duration]);
  const initial = useMemo(
    () => [show.startOffsetSec, Math.min(show.endOffsetSec, show.startOffsetSec + DEFAULT_CLIP_SEC)],
    [show],
  );
  const [range, setRange] = useState(initial);
  const [playhead, setPlayhead] = useState(show.startOffsetSec);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Read by the timeupdate handler so dragging the out-point during a preview
  // moves where it stops, instead of using the value captured when play began.
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const lengthSec = range[1] - range[0];
  const tooLong = lengthSec > MAX_CLIP_SEC;
  const tooShort = lengthSec < MIN_CLIP_SEC;
  const blocked = tooLong || tooShort;

  const seek = useCallback((sec) => {
    const v = videoRef.current;
    if (!v) return;
    setPlayhead(sec);
    try {
      v.currentTime = sec;
    } catch (_) {
      /* metadata not ready yet */
    }
  }, []);

  const stopPreview = useCallback(() => {
    previewingRef.current = false;
    setPreviewing(false);
    videoRef.current?.pause();
  }, []);

  const setStart = useCallback(() => {
    setRange(([, end]) => withStart(bounds, playhead, end));
  }, [bounds, playhead]);

  const setEnd = useCallback(() => {
    setRange(([start]) => withEnd(bounds, start, playhead));
  }, [bounds, playhead]);

  // I / O are the in-point / out-point keys every editing tool already uses.
  // The button labels advertise them, which is what makes them worth wiring.
  useEffect(() => {
    if (confirming) return; // the dialog owns the keyboard while it is open
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      // e.code, not e.key: on a Hebrew layout those keys report ן/ם.
      if (e.code === "KeyI") {
        e.preventDefault();
        setStart();
      }
      if (e.code === "KeyO") {
        e.preventDefault();
        setEnd();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setStart, setEnd, confirming]);

  // Dragging a handle seeks to it, so you always see the frame you are choosing.
  // Without this the handles are blind and the only way to judge a boundary is
  // to play up to it and mark — which is what made the mark buttons feel load-
  // bearing rather than a shortcut.
  const handleRangeChange = (_e, value, activeThumb) => {
    if (!Array.isArray(value)) return;
    setRange(value);
    const target = value[activeThumb === 1 ? 1 : 0];
    if (target !== playheadRef.current) {
      playheadRef.current = target;
      seek(target);
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setPlayhead(v.currentTime);
    if (previewingRef.current && v.currentTime >= rangeRef.current[1]) stopPreview();
  };

  const togglePreview = () => {
    const v = videoRef.current;
    if (!v) return;
    if (previewing) {
      stopPreview();
      return;
    }
    seek(range[0]);
    previewingRef.current = true;
    setPreviewing(true);
    v.play().catch(() => stopPreview());
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await generateFromClip({
        key: show.recordingKey,
        startSec: Math.round(range[0]),
        endSec: Math.round(range[1]),
      });
      addToaster({ message: text.queued, severity: "success" });
      setConfirming(false);
      onGenerated();
    } catch (_) {
      setConfirming(false); // toaster raised by the hook
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Button
          size="small"
          onClick={onBack}
          startIcon={
            <ArrowBackRoundedIcon
              fontSize="small"
              sx={{ transform: direction === "rtl" ? "scaleX(-1)" : "none" }}
            />
          }
        >
          {text.back}
        </Button>
        <Divider orientation="vertical" flexItem />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {show.title || text.untitled}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", ...LTR }}>
          {showWindow(show)}
        </Typography>
      </Box>

      <Box sx={{ bgcolor: "common.black", borderRadius: 1, overflow: "hidden" }}>
        <video
          ref={videoRef}
          src={show.url}
          controls
          preload="metadata"
          onLoadedMetadata={() => {
            const d = videoRef.current?.duration;
            if (Number.isFinite(d) && d > 0) setDuration(d);
            seek(range[0]);
          }}
          onTimeUpdate={handleTimeUpdate}
          onSeeked={handleTimeUpdate}
          // Pausing leaves preview mode entirely — otherwise the auto-stop stays
          // armed and a later normal play would halt at the selection end.
          onPause={() => {
            previewingRef.current = false;
            setPreviewing(false);
          }}
          onEnded={stopPreview}
          style={{ width: "100%", maxHeight: 440, display: "block" }}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <TrimTimeline
          show={show}
          bounds={bounds}
          range={range}
          playhead={playhead}
          onSeek={seek}
          onRangeChange={handleRangeChange}
          text={text}
        />

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          <TimeField
            label={text.start}
            value={range[0]}
            maxSec={bounds.max}
            text={text}
            onCommit={(v) => setRange(([, end]) => withStart(bounds, v, end))}
          />
          <TimeField
            label={text.end}
            value={range[1]}
            maxSec={bounds.max}
            text={text}
            onCommit={(v) => setRange(([start]) => withEnd(bounds, start, v))}
          />
          <Box sx={{ pt: 1 }}>
            <Chip
              color={blocked ? "error" : "primary"}
              label={`${text.length} ${fmtClock(lengthSec)}`}
              sx={{ ...LTR, fontWeight: 600 }}
            />
          </Box>
          <Box sx={{ pt: 1.25, marginInlineStart: "auto" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", ...LTR }}>
              {text.aired} {fmtWall(show.recordStartUtcMs, range[0])}–
              {fmtWall(show.recordStartUtcMs, range[1])}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {/* Shortcut for marking a boundary while the video is playing — the
              one thing dragging a handle cannot do. */}
          <Tooltip title={text.setStartHint}>
            <Button size="small" variant="outlined" onClick={setStart} startIcon={<FirstPageRoundedIcon fontSize="small" />}>
              {text.setStart}
            </Button>
          </Tooltip>
          <Tooltip title={text.setEndHint}>
            <Button size="small" variant="outlined" onClick={setEnd} startIcon={<LastPageRoundedIcon fontSize="small" />}>
              {text.setEnd}
            </Button>
          </Tooltip>
          <Button
            size="small"
            variant={previewing ? "contained" : "outlined"}
            color={previewing ? "error" : "primary"}
            onClick={togglePreview}
            startIcon={
              previewing ? (
                <StopRoundedIcon fontSize="small" />
              ) : (
                <PlayArrowRoundedIcon fontSize="small" />
              )
            }
          >
            {previewing ? text.stop : text.preview}
          </Button>
          <Button
            size="small"
            onClick={() => setRange(initial)}
            startIcon={<RestartAltRoundedIcon fontSize="small" />}
          >
            {text.reset}
          </Button>

          {/* marginInlineStart, not marginLeft: the primary action sits at the
              end of the row, which is the right in English and the left in
              Hebrew. */}
          <Button
            variant="contained"
            color="success"
            disabled={blocked}
            onClick={() => setConfirming(true)}
            startIcon={<AutoAwesomeRoundedIcon fontSize="small" />}
            sx={{ marginInlineStart: "auto" }}
          >
            {text.generate}
          </Button>
        </Stack>

        {tooLong && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {text.tooLong.replace("{over}", fmtClock(lengthSec - MAX_CLIP_SEC))}
          </Alert>
        )}
        {tooShort && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {text.tooShort.replace("{min}", fmtClock(MIN_CLIP_SEC))}
          </Alert>
        )}
      </Paper>

      <ConfirmGenerateDialog
        open={confirming}
        show={show}
        range={range}
        submitting={submitting}
        blocked={blocked}
        onCancel={() => setConfirming(false)}
        onConfirm={handleConfirm}
        text={text}
      />
    </Stack>
  );
}
